// CatalogueUtils.tsx
import { conns } from "@cfg/axiosConfig.tsx";
import { logError, logInfo } from "@tools/LogsHandler";
import {
    Avatar,
    Badge,
    Button,
    Card,
    Carousel,
    Dropdown,
    Form,
    FormInstance,
    Image,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Tooltip,
    Tag,
    Typography,
} from 'antd';
import {
    Child,
    NewLineType,
    Item,
    ItemOption,
    ItemOptionsFormModalProp,
    PromotionLite,
    StockStateKey,
} from "@eshop/types/catalogue.types.ts";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { parsePrice, slugify } from "@tools/utils/common.tsx";
import { getImageUrl, IMG_NOT_FOUND } from '@tools/utils/imageUtils.tsx';
import { AppDispatch, RootState, useAppDispatch, useAppSelector } from "@store/store.tsx";
import { CartItem, ItemOptionValue } from "@eshop/types/cart.types.ts";
import { getItemPrice } from "@eshop/components/catalogue/Cataloguefunctions.tsx";
import { useSelector } from "react-redux";
import { removeFromCartAsync, sendNewLineAsync, setDefaultsValues, type RemoveFromCartPayload } from "@store/slices/cartSlice.tsx";
import { CloseCircleOutlined, MinusOutlined, PlusOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import { updateCartItemQuantity } from "@eshop/components/cart/cartUtils.tsx";
import { CalendarInput, CalendarTimeInput } from '@tools/components/calendar.tsx';
import { ImageInfo } from '@app-types/commons/images.types.ts';
import { useThemeMode } from '@theme/hooks/useThemeMode.tsx';
import {
    selectBlockSalesWithoutStockSetting,
    selectErpEnabled,
} from '@store/slices/settingsSlice.tsx';
import useSiteLabels from '@eshop/hooks/useSiteLabels.ts';
import {
    createStockStateCopy,
} from '@eshop/utils/stockPresentation.ts';
import {
    resolveBlockedCatalogueMessage,
    resolveCataloguePurchaseState,
} from './purchaseState';
import {
    trackCartItemAdded,
} from '@eshop/utils/itemAnalytics.ts';

const defaultRemovalErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object') {
        const maybeAxiosLike = error as { message?: string; response?: { data?: { detail?: string; message?: string } } };
        const detail = maybeAxiosLike.response?.data?.detail;
        if (typeof detail === 'string' && detail.trim().length > 0) {
            return detail;
        }
        const responseMessage = maybeAxiosLike.response?.data?.message;
        if (typeof responseMessage === 'string' && responseMessage.trim().length > 0) {
            return responseMessage;
        }
        if (typeof maybeAxiosLike.message === 'string' && maybeAxiosLike.message.trim().length > 0) {
            return maybeAxiosLike.message;
        }
    }
    if (typeof error === 'string' && error.trim().length > 0) {
        return error;
    }
    return 'No pudimos quitar el producto del carrito. Intentá nuevamente.';
};

type CartLineRemovalResult =
    | { success: true }
    | { success: false; previousQuantity: number; errorMessage: string };

interface AttemptCartLineRemovalParams {
    cartItem: CartItem;
    dispatch: AppDispatch;
    resolveErrorMessage?: (error: unknown) => string;
}

/**
 * Intenta eliminar la línea del carrito en backend y devuelve si se concretó.
 * - En caso de error, muestra feedback y retorna la cantidad previa para rollback.
 */
export const attemptCartLineRemoval = async ({
    cartItem,
    dispatch,
    resolveErrorMessage,
}: AttemptCartLineRemovalParams): Promise<CartLineRemovalResult> => {
    const previousQuantity = cartItem.quantity;
    const resolveMessage = resolveErrorMessage ?? defaultRemovalErrorMessage;

    try {
        const action = await dispatch(
            removeFromCartAsync({ itemId: cartItem.item_id, lineId: cartItem.id } satisfies RemoveFromCartPayload),
        );

        if (removeFromCartAsync.fulfilled.match(action)) {
            logInfo('catalogue:cart_line_removed', { lineId: cartItem.id, itemId: cartItem.item_id });
            return { success: true };
        }

        const rejectionCause = action.payload ?? action.error;
        const errorMessage = resolveMessage(rejectionCause);
        void message.error(errorMessage);
        logError('catalogue:cart_line_remove_failed', {
            lineId: cartItem.id,
            itemId: cartItem.item_id,
            error: rejectionCause,
        });
        return { success: false, previousQuantity, errorMessage };
    } catch (error) {
        const errorMessage = resolveMessage(error);
        void message.error(errorMessage);
        logError('catalogue:cart_line_remove_failed', {
            lineId: cartItem.id,
            itemId: cartItem.item_id,
            error,
        });
        return { success: false, previousQuantity, errorMessage };
    }
};

/** Convierte un Child a Item, normalizando id numérico.
 *    In: Child / Out: Item */
export const childToProd = (child: Child): Item => {
    const id = typeof child.id === 'string' ? parseInt(child.id, 10) : child.id;
    const normalizedStock = typeof child.stock === 'number' ? child.stock : null;
    const canBuy = child.can_buy ?? (normalizedStock === null ? true : normalizedStock > 0);
    return {
        ...child,
        id: Number.isFinite(id as number) ? (id as number) : 0,
        upc: child.upc ? child.upc : '',
        description: '',
        disponibilidad: canBuy,
        stock: normalizedStock,
        can_buy: canBuy,
        stock_state: child.stock_state ?? null,
        is_preview: false,
        is_child: true,
        parent: undefined,
        item_type: child.item_type || 'product',
    } as unknown as Item;
};

export const buildItemDetailUrl = (item: Item): string => {
    const slug = slugify(item.name);
    return `/eshop/detail/${slug}_${item.id}`;
};

export const requestStockAlert = (item: Item, parent?: Item | null) => {
    const parentId = parent?.id ?? (item.is_child ? item.parent?.id ?? null : null);
    logInfo('catalogue:stock_alert_requested', {
        itemId: item.id,
        parentId,
        stockState: item.stock_state ?? null,
    });
    message.success('Listo, te avisaremos cuando vuelva el stock.');
};

interface ItemImageProps {
    image?: ImageInfo;
    preview?: React.ComponentProps<typeof Image>['preview'];
    width?: number | string;
    height?: number | string;
    className?: string;
    avatar?: boolean;
    onPreviewOpen?: () => void;
}

/** Muestra una imagen con manejo de fallback y <srcSet>. */
export const ItemImage: React.FC<ItemImageProps> = ({
    image,
    preview = true,
    width = '100%',
    height = '100%',
    className = 'img_cover bg-light',
    avatar = false,
    onPreviewOpen,
}) => {
    /** Solo queremos un reintento; con 0 → 1 pasamos a fallback. */
    const [errorCount, setErrorCount] = useState(0);
    const { srcDefault, srcSet } = getImageUrl(image);

    /** Pasa a imagen de reserva y vacía el srcSet para evitar nuevos 404. */
    const handleImageError = (evt: React.SyntheticEvent<HTMLImageElement>) => {
        if (errorCount === 0) {                       // un solo intento extra
            setErrorCount(1);
            logError('Error loading image:', image);
            const img = evt.currentTarget;
            img.src = IMG_NOT_FOUND;
            img.srcset = '';
        }
    };

    if (avatar) {
        return (
            <Avatar
                size={Number(width) || 100}
                className={className}
                src={srcDefault}
                srcSet={srcSet}
                alt={image?.caption ?? 'Imagen sin descripción'}
                shape="square"
            />
        );
    }

    return (
        <Image
            width={width}
            height={height}
            className={className}
            preview={preview}
            src={srcDefault}
            srcSet={srcSet}
            alt={image?.caption ?? 'Imagen sin descripción'}
            onError={handleImageError}
            onClick={preview && onPreviewOpen ? onPreviewOpen : undefined}
            fallback={IMG_NOT_FOUND}
        />
    );
};

interface CarouselItemProps {
    item: Item;
    selectedChild: Item | null;
    aspect?: string;
    effect?: 'scrollx' | 'fade'; // Propiedad para seleccionar el tipo de efecto
    preview?: boolean;
    onPreviewOpen?: () => void;
}

/** Carrusel de imágenes combinando del item y sus variantes. In: props / Out: JSX */
export const CarrouselItem: React.FC<CarouselItemProps> = ({
    item,
    selectedChild,
    aspect = 'aspect_1-1',
    effect = 'scrollx',
    preview = true,
    onPreviewOpen,
}) => {
    const [images, setImages] = useState<ImageInfo[]>(item.images || []);

    useEffect(() => {
        if (selectedChild) {
            setImages(selectedChild.images || item.images || []);
        } else {
            let combinedImages: ImageInfo[] = [...(item.images || [])];

            if (item.children && item.children.length > 0) {
                const childImages = item.children
                    .flatMap(child => (child.images && child.images[0] ? [child.images[0]] : []))
                    .slice(0, Math.max(10 - combinedImages.length, 0));

                combinedImages = [...combinedImages, ...childImages].slice(0, 10);
            }

            setImages(combinedImages);
        }
    }, [selectedChild, item.children, item.images]);

    // Genera una clave única basada en las imágenes para forzar la actualización del carrusel
    const carouselKey = images.map((image) => image.thumbnails?.urls.small).join(',');

    return images.length > 0 ? (
        <Carousel key={carouselKey} autoplay className="w-100 h-100" effect={effect}>
            {images.map((image, index) => (
                <div
                    key={image.id ?? image.thumbnails?.urls.small ?? `image-${index}`}
                    className={`contenedor ${aspect}`}
                >
                    <div className="contenido">
                        {!preview && item.url ? (
                            // Si preview está desactivado y existe una URL, redirige a la URL al hacer clic en la imagen
                            <a href={item.url}>
                                <ItemImage image={image} preview={preview} />
                            </a>
                        ) : (
                            // Si preview está activado o no hay URL, solo muestra la imagen
                            <ItemImage image={image} preview={preview} onPreviewOpen={onPreviewOpen} />
                        )}
                    </div>
                </div>
            ))}
        </Carousel>
    ) : (
        <div className={`contenedor ${aspect}`}>
            <div className="contenido">
                <Image
                    src={IMG_NOT_FOUND}
                    width="100%"
                    height="100%"
                    className="img_cover bg-light"
                    alt={item.name}
                />
            </div>
        </div>
    );
};


interface ItemOptionsFormProp {
    item: Item,
    optionValues: ItemOptionValue,
    setValue: (values: ItemOptionValue) => void, form: FormInstance
}
/** Renderiza campos de opciones del item. In: props / Out: JSX */
const RenderItemOptionsForm: React.FC<ItemOptionsFormProp> = ({
    item,
    optionValues,
    setValue,
    form
}) => {
    const diasEntrega = useSelector((state: RootState) => state.eshop.cfg.dias_entrega);
    const horarios = useSelector((state: RootState) => state.eshop.cfg.horarios);
    const defaults = useSelector((state: RootState) => state.cart.defaults);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formRefs = useRef<{ [key: string]: any }>({});


    if (!item.options || item.options.length === 0) {
        return null; // Siempre devuelve algo, aunque sea null
    }

    const formItems = item.options.map(op => {

        let fieldInput;
        let default_value;
        switch (op.format) {
            case 'date_future':
                default_value = defaults?.date ?? undefined;
                if (default_value) optionValues[op.code] = default_value;
                fieldInput = <div className="d-flex justify-content-center">
                    <CalendarInput key={op.code} op={op} dictValues={optionValues} limitDate={true} days={diasEntrega} horarios={horarios} />
                </div>;
                break;
            case 'date':
                default_value = defaults?.date ?? undefined;
                if (default_value) optionValues[op.code] = default_value;
                fieldInput = <div className="d-flex justify-content-center">
                    <CalendarInput key={op.code} op={op} dictValues={optionValues} horarios={horarios} />
                </div>;
                break;
            case 'date_hour_future':
                default_value = defaults?.datetime ?? undefined;
                if (default_value) optionValues[op.code] = default_value;
                fieldInput = <div className="d-flex justify-content-center">
                    <CalendarTimeInput key={op.code} op={op} dictValues={optionValues} limitDate={true} days={diasEntrega} horarios={horarios}
                        ref={// eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (el: any) => formRefs.current[op.code] = el}
                        form={form} />
                </div>;
                break;
            case 'date_hour':
                default_value = defaults?.datetime ?? undefined;
                if (default_value) optionValues[op.code] = default_value;
                fieldInput = <div className="d-flex justify-content-center">
                    <CalendarTimeInput key={op.code} op={op} dictValues={optionValues} horarios={horarios}
                        ref={// eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (el: any) => formRefs.current[op.code] = el}
                        form={form} />
                </div>;
                break;
            case 'text':
            default:
                fieldInput = <Input type={op.format}
                    className="w-100"
                    key={op.code}
                    name={op.code}
                    onChange={e =>
                        setValue({ ...optionValues, [op.code]: e.target.value })} />;
                break;
        }
        return (
            <Form.Item
                label={op.name}
                key={op.code}
                name={op.code}
            // rules={[{ required: op.type.toLowerCase() === 'required', message: `${op.name} es requerido.` }]}
            >
                {fieldInput}
            </Form.Item>
        );
    });

    return <>{formItems}</>; // Envuelve los items de formulario en un fragmento
};

/** Actualiza defaults (date/datetime) en Redux según opciones elegidas. In: options, value, dispatch / Out: void */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const updateDefaults = (options: ItemOption[], optionValue: ItemOptionValue, dispatch: any) => {

    options.forEach((op) => {
        if (['date', 'date_future'].includes(op.format) && optionValue[op.code]) {
            // Aquí se actualizan los defaults en el estado global
            const dateValue = optionValue[op.code];
            if (dateValue) {
                dispatch(setDefaultsValues({ date: dateValue }));
            }
        }
        if (['date_hour', 'date_hour_future'].includes(op.format) && optionValue[op.code]) {
            // Aquí se actualizan los defaults en el estado global
            const dateValue = optionValue[op.code];
            if (dateValue) {
                dispatch(setDefaultsValues({ datetime: dateValue }));
            }
        }
    });
};

/** Modal para opciones del item; al confirmar, dispara add line. In: props / Out: JSX */
export const ItemOptionsFormModal: React.FC<ItemOptionsFormModalProp> = ({ item, isModalVisible, setIsModalVisible, newLine }) => {
    const dispatch = useAppDispatch();
    const [itemOptionValue, setItemOptionValue] = useState<ItemOptionValue>({});
    const [form] = Form.useForm();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formRefs = useRef<{ [key: string]: any }>({});

    if (!item.options || item.options.length === 0) {
        return null; // Siempre devuelve algo, aunque sea null
    }

    const handleOk = async () => {
        try {
            await form.validateFields();
            let isValid = true;
            Object.values(formRefs.current).forEach(ref => {
                if (ref && ref.validate) { if (!ref.validate()) { isValid = false; } }
            });

            if (!isValid) {
                throw new Error('Validation failed');
            }

            setIsModalVisible(false);
            dispatch(sendNewLineAsync({
                item,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                quantity: (newLine as any)?.quantity ?? 1,
                optionsInfo: itemOptionValue
            }));
            if (item.options) updateDefaults(item.options, itemOptionValue, dispatch);
        } catch (error) {
            logError('Error de validación - error:', error);
            message.error('Por favor complete todos los campos requeridos.');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };


    return isModalVisible ? (
        <Modal title="Información Adicional" open={isModalVisible} onOk={handleOk} onCancel={handleCancel}>
            <Form form={form} layout="vertical">
                <RenderItemOptionsForm item={item} optionValues={itemOptionValue} setValue={setItemOptionValue} form={form} />
            </Form>
        </Modal>
    ) : null;
};


interface SelectVariantsItemProp {
    item: Item;
    selectedChild: Item | null;
    setSelectedChild: (selectedChild: Item | null) => void;
    className?: string;
}

export const buildVariantSelectionSummary = (selectedChild: Item | null): Child | null => {
    if (!selectedChild) {
        return null;
    }

    return {
        id: selectedChild.id,
        name: selectedChild.name,
        stock: selectedChild.stock ?? null,
        can_buy: selectedChild.can_buy ?? null,
        stock_state: selectedChild.stock_state ?? null,
    };
};

export const hasSameVariantSelectionSummary = (left: Child | null, right: Child | null): boolean => {
    if (!left || !right) {
        return left === right;
    }

    return (
        String(left.id) === String(right.id) &&
        left.name === right.name &&
        (left.stock ?? null) === (right.stock ?? null) &&
        (left.can_buy ?? null) === (right.can_buy ?? null) &&
        (left.stock_state ?? null) === (right.stock_state ?? null)
    );
};

/** Selector de variantes; carga preview o data completa del hijo. In: props / Out: JSX */
export const SelectVariantsItem: React.FC<SelectVariantsItemProp> = ({ item, selectedChild, setSelectedChild, className = '' }) => {
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const cartItems = useSelector((state: RootState) => state.cart.cartItems);
    const [tempSelectedChild, setTempSelectedChild] = useState<Child | null>(() =>
        buildVariantSelectionSummary(selectedChild)
    );
    const prodInCart = cartItems.filter(ci => ci.main_item_id === item.id).length;

    // Sync tempSelectedChild with selectedChild whenever selectedChild changes
    useEffect(() => {
        const nextSummary = buildVariantSelectionSummary(selectedChild);
        setTempSelectedChild((current) =>
            hasSameVariantSelectionSummary(current, nextSummary) ? current : nextSummary
        );
    }, [
        selectedChild?.id,
        selectedChild?.name,
        selectedChild?.stock,
        selectedChild?.can_buy,
        selectedChild?.stock_state,
    ]);

    const { catalog } = useSiteLabels();
    const catalogLabels = catalog.labels;
    const catalogFlags = catalog.flags;
    const stockCopy = useMemo(
        () => createStockStateCopy(catalogLabels, catalogFlags),
        [catalogFlags, catalogLabels],
    );

    const renderOptions = (child: Child) => {
        const childInCart = cartItems.find(ci => ci.item_id === child.id);
        const childState = (child.stock_state ?? undefined) as StockStateKey | undefined;
        const stateBadge =
            childState && childState !== 'in_stock'
                ? stockCopy[childState]
                : undefined;
        const isChildOutOfStock = child.can_buy === false || childState === 'out_of_stock';
        const strikeClass = isChildOutOfStock ? "text-danger text-decoration-line-through" : "";
        const content = childInCart ? (
            <>
                <Badge count={childInCart.quantity} color="red" className="site-badge-count-4" />
                <span className={`${strikeClass}`} style={{ marginLeft: 8 }}>{child.name}</span>
            </>
        ) : <span className={`${strikeClass}`}>{child.name}</span>;

        return (
            <>
                {content}
                {stateBadge?.label && (
                    <Tag color={stateBadge.color} className="ms-2">
                        {stateBadge.label}
                    </Tag>
                )}
            </>
        );
    };

    const handleSelectChild = useCallback(async (childId: number | string | null) => {
        logInfo('catalogue:variant:select', { itemId: item.id, childId });
        if (childId === null) {
            setTempSelectedChild((current) => current === null ? current : null);
            if (selectedChild !== null) {
                setSelectedChild(null);
            }
            return;
        }
        if (item.children) {
            const child = item.children.find(c => c.id === childId);
            if (child) {
                if (String(selectedChild?.id) === String(child.id)) {
                    const nextSummary = buildVariantSelectionSummary(selectedChild);
                    setTempSelectedChild((current) =>
                        hasSameVariantSelectionSummary(current, nextSummary) ? current : nextSummary
                    );
                    return;
                }

                setTempSelectedChild((current) =>
                    hasSameVariantSelectionSummary(current, child) ? current : child
                );
                if (child.is_preview) {
                    setSelectedChild(childToProd(child));
                    return;
                }
                const childResponse = await conns<Item>('eshop:catalogue:get_data_from_id', [childId]).get();
                logInfo('catalogue:variant:fetched', { itemId: item.id, childId });
                const fetchedSummary = {
                    id: childResponse.data.id,
                    name: childResponse.data.name,
                    stock: childResponse.data.stock ?? null,
                    can_buy: childResponse.data.can_buy ?? null,
                    stock_state: childResponse.data.stock_state ?? null,
                };
                setTempSelectedChild((current) =>
                    hasSameVariantSelectionSummary(current, fetchedSummary) ? current : fetchedSummary
                );
                setSelectedChild(childResponse.data);
            } else {
                message.info('No logró encontrarse la selección.');
            }
        } else {
            setTempSelectedChild((current) => current === null ? current : null);
            if (selectedChild !== null) {
                setSelectedChild(null);
            }
        }
    }, [item.children, item.id, selectedChild, setSelectedChild]);

    if (!item.children || !item.children.length) return null;

    const items = item.children.map(child => ({
        key: child.id.toString(),
        label: (
            <div className={child.id == selectedChild?.id ? "bg_web_color_claro_2" : ""} style={{ whiteSpace: 'normal', maxWidth: '350px' }}>
                {renderOptions(child)}
            </div>
        ),
        onClick: () => handleSelectChild(child.id)
    }));

    return (
        <Badge count={prodInCart} color="red" className="site-badge-count-4"
            styles={{ indicator: { zIndex: 1 }, root: { maxWidth: '100%' } }}>
            <Dropdown menu={{ items }} open={dropdownVisible} onOpenChange={setDropdownVisible}>
                <Button style={{ maxWidth: '100%' }}>
                    <span className={`${className} text-truncate ${tempSelectedChild?.can_buy === false ? "text-danger text-decoration-line-through" : ""}`}>
                        {tempSelectedChild?.name || "Seleccione uno"}
                    </span>
                    {tempSelectedChild && (
                        <CloseCircleOutlined
                            style={{ marginLeft: 8 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectChild(null).then();
                            }}
                        />
                    )}
                </Button>
            </Dropdown>
        </Badge>
    );
};

interface ItemProps {
    item: Item;
    selectedChild: Item | null;
    setSelectedChild: (i: Item | null) => void
}
/** Card de producto con control de carrito, variantes, precio y promociones */
const ItemCard: React.FC<ItemProps> = ({ item, selectedChild, setSelectedChild }) => {
    const item_aspect_card = useSelector((state: RootState) => state.eshop.cfg.item_aspect_card);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [newLine, setNewLine] = useState<NewLineType | null>(null);

    const { Meta } = Card;
    const dispatch = useAppDispatch();

    // Primero, obtén el estado del carrito completo.
    const cartItems = useSelector((state: RootState) => state.cart.cartItems);
    const cartItem = cartItems.find(ci => ci.item_id === (selectedChild?.id || item.id));

    // ✅ MEJORA 1: Determinar si tiene variantes automáticamente
    const hasVariants = !!(item.children && item.children.length > 0);

    const hasOptions = !!(item.options && item.options.length);


    // Verificar y establecer el primer hijo en el carrito como selectedChild
    useEffect(() => {
        const firstChildInCart = cartItems.find((child: CartItem) =>
            item.children?.some(it => child.item_id === it.id)
        );

        if (firstChildInCart && !selectedChild && firstChildInCart.item) {
            setSelectedChild(firstChildInCart.item);
        }
    }, [cartItems, item.children, selectedChild]);   // eslint-disable-line react-hooks/exhaustive-deps

    const get_prod = () => (selectedChild ? { ...selectedChild, parent: { id: item.id, name: item.name, image: item.images && item.images[0] } } : { ...item });

    const handleAddToCart = async () => {
        if (!item) { message.info('E10001: Error al intentar agregar el producto.'); return; }
        const item_to_add_local = get_prod();
        const priceInfo = parsePrice(item_to_add_local.price);

        if (!priceInfo) {
            message.info('Este producto no está disponible temporalmente.').then();
            return;
        }

        if (!stockPresentation.canBuy) {
            const blockedMessage = resolveBlockedCatalogueMessage(stockPresentation, catalogLabels);
            if (blockedMessage) {
                message.info(blockedMessage).then();
            }
            logInfo('catalogue:add_blocked', {
                itemId: item_to_add_local.id,
                reason: 'stock',
                stockState: item_to_add_local.stock_state ?? null,
            });
            return;
        }

        if (hasVariants && !selectedChild) {
            message.info('Este producto necesita que elija una de las opciones disponibles.').then();
            logInfo('catalogue:add_blocked', {
                itemId: item.id,
                reason: 'variant_required',
            });
            return;
        }

        const name =
            item_to_add_local.fullName ||
            item_to_add_local.full_name ||
            (item_to_add_local.parent
                ? `${item_to_add_local.parent.name} - ${item_to_add_local.name}`
                : item_to_add_local.name);

        const newLineLocal = {
            item_id: item_to_add_local.id,
            main_item_id: item.id,
            name: name,
            quantity: 1,
            price: priceInfo.value,
            item: item_to_add_local,
        }
        setNewLine(newLineLocal);
        trackCartItemAdded(item_to_add_local, 1, { surface: 'catalog', source: 'legacy_card' });

        if (item_to_add_local.options && item_to_add_local.options.length > 0) {
            setIsModalVisible(true);
        } else {
            dispatch(sendNewLineAsync({ item: item_to_add_local, quantity: 1 }));
        }
    };

    const [quantity, setQuantity] = useState(cartItem ? cartItem.quantity : 0);
    const [errorMsg, setErrorMsg] = useState<string | undefined>();
    // Aplica debouncing a la cantidad
    const [debouncedQuantity] = useDebounce<number>(quantity, 500);

    useEffect(() => {
        if (cartItem && debouncedQuantity !== cartItem.quantity) { // Solo sincroniza si la cantidad debounced es diferente a la actual en el carrito
            const item_to_add_local = get_prod();
            logInfo('catalogue:quantity:update', {
                itemId: item_to_add_local.id,
                quantity: debouncedQuantity,
            });
            if (debouncedQuantity > 0) {
                updateCartItemQuantity(
                    cartItem,
                    debouncedQuantity,
                    dispatch,
                    setQuantity,
                    setErrorMsg
                ).then();
            } else {
                // Cantidad 0 → pedimos eliminar la línea y restablecemos la UI si la API rechaza.
                void (async () => {
                    const result = await attemptCartLineRemoval({ cartItem, dispatch });
                    if (!result.success) {
                        setQuantity(result.previousQuantity);
                        setErrorMsg(result.errorMessage);
                    } else {
                        setErrorMsg(undefined);
                    }
                })();
            }
        }
    }, [cartItem, debouncedQuantity, dispatch, item.id]);

    useEffect(() => {
        if (cartItem) {
            setQuantity(cartItem.quantity);
        }
    }, [cartItem]);

    const handleQuantityChange = (newQuantity: number | null) => {
        if (newQuantity === null) { return; }
        if (newQuantity >= 0) {
            setQuantity(newQuantity);
        }
    };

    const item_to_add = get_prod();
    const effectiveItem = selectedChild || item;
    const erpEnabled = useAppSelector(selectErpEnabled);
    const blockSalesWithoutStock = useAppSelector(selectBlockSalesWithoutStockSetting);
    const { catalog } = useSiteLabels();
    const catalogLabels = catalog.labels;
    const catalogFlags = catalog.flags;
    const {
        stockPresentation,
        requiresVariantSelection,
        canPurchase,
        effectiveStock,
    } = useMemo(
        () =>
            resolveCataloguePurchaseState({
                baseItem: item,
                currentItem: effectiveItem,
                hasVariants,
                hasSelectedVariant: Boolean(selectedChild),
                erpEnabled,
                blockSalesWithoutStock,
                labels: catalogLabels,
                flags: catalogFlags,
            }),
        [
            blockSalesWithoutStock,
            catalogFlags,
            catalogLabels,
            effectiveItem,
            erpEnabled,
            hasVariants,
            item,
            selectedChild,
        ],
    );

    const handleDecrement = () => {
        if (!cartItem) {
            if (quantity > 0) {
                handleQuantityChange(quantity - 1);
            }
            return;
        }

        if (quantity <= 1) {
            void (async () => {
                const result = await attemptCartLineRemoval({ cartItem, dispatch });
                if (!result.success) {
                    setQuantity(result.previousQuantity);
                    setErrorMsg(result.errorMessage);
                } else {
                    setErrorMsg(undefined);
                }
            })();
            return;
        }

        handleQuantityChange(quantity - 1);
    };

    const handleIncrement = () => {
        if (!stockPresentation.canBuy) {
            const blockedMessage = resolveBlockedCatalogueMessage(stockPresentation, catalogLabels);
            setErrorMsg(blockedMessage ?? undefined);
            logInfo('catalogue:quantity_blocked', {
                itemId: item_to_add.id,
                reason: 'stock',
                stockState: effectiveItem.stock_state ?? null,
            });
            return;
        }
        if (typeof effectiveStock === 'number' && stockPresentation.state === 'in_stock' && quantity >= effectiveStock) {
            setErrorMsg('Cantidad máxima disponible en stock.');
            logInfo('catalogue:quantity_blocked', {
                itemId: item_to_add.id,
                reason: 'limit',
                stock: effectiveStock,
            });
            return;
        }
        handleQuantityChange(quantity + 1);
    };

    const cartControl = cartItem ? (
        <Popconfirm title={errorMsg} open={!!errorMsg} showCancel={false} onConfirm={() => setErrorMsg(undefined)}>
            <div className="w-100 d-flex justify-content-center">
                <Button icon={<MinusOutlined />} onClick={handleDecrement} />
                <InputNumber
                    min={1}
                    max={100}
                    value={quantity}
                    className="ant_input_text_align_center"
                    style={{ width: '4rem' }}
                    onChange={handleQuantityChange}
                    controls={false}
                />
                <Button icon={<PlusOutlined />} onClick={handleIncrement} />
            </div>
        </Popconfirm>
    ) : (
        requiresVariantSelection || !canPurchase ? (
            <Tooltip
                title={requiresVariantSelection
                    ? "Debes elegir una opción"
                    : resolveBlockedCatalogueMessage(stockPresentation, catalogLabels) ?? undefined}
                color={!stockPresentation.canBuy ? "red" : "light"}
            >
                <Button
                    className="w-100 h-100 text-black-50 m-0 p-0 border-0 shadow-none"
                    icon={<ShoppingCartOutlined style={{ fontSize: '32px' }} />}
                    disabled
                    onClick={() => {
                        logInfo('catalogue:add_blocked', {
                            itemId: item.id,
                            reason: requiresVariantSelection ? 'variant_required' : 'stock',
                            stockState: effectiveItem.stock_state ?? null,
                        });
                    }}
                />
            </Tooltip>
        ) : (
            <>
                <Button
                    className="addToCartButton w-100 h-100 m-0 p-0 text_web_color_oscuro_1 hover_web_color_oscuro_1 border-0 shadow-none"
                    icon={<ShoppingCartOutlined style={{ fontSize: '32px' }} />}
                    onClick={handleAddToCart}
                />
                {hasOptions && newLine && (
                    <ItemOptionsFormModal
                        item={item_to_add}
                        isModalVisible={isModalVisible}
                        setIsModalVisible={setIsModalVisible}
                        newLine={newLine}
                    />
                )}
            </>
        )
    );

    const getTitle = (selectedChild ?
        <span>{item.name}<br /><small>{selectedChild?.name}</small></span> :
        <div>{item.name}</div>)

    const formatedPrice = getItemPrice(selectedChild || item);
    const itemUrl = item.url || buildItemDetailUrl(item);

    // Verifica cambios en los children para actualizar el selectedChild
    useEffect(() => {
        if (selectedChild && item.children && item.children.length > 0) {
            const newSelectedChild = item.children.find(it => selectedChild.id === it.id);
            if (newSelectedChild && newSelectedChild.is_preview)
                setSelectedChild(childToProd(newSelectedChild));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.children]);

    // ✅ Renderizado de precio con promociones
    const renderPrice = () => {
        if (!formatedPrice) return null;

        const { value, formattedPrice, promotion } = formatedPrice;

        if (promotion) {
            const { promotion: bestPromo, formatedDiscountedPrice, /*discount*/ } = promotion;
            const isConditionalPrice = Boolean(
                promotion.conditional_price
                || bestPromo.conditional_price
                || bestPromo.display_type === 'conditional_catalog_price',
            );
            // const discountPercentage = Math.round((discount / value) * 100);

            return (
                <div className="text-center w-100">
                    {/* Precio original tachado */}
                    <div className="text_web_color_oscuro_1 text-decoration-line-through h5 mb-1">
                        {formattedPrice}
                    </div>

                    {/*/!* Badge de descuento *!/*/}
                    {/*<div className="text_web_color_oscuro_3 small mb-1">*/}
                    {/*    {discountPercentage}% OFF*/}
                    {/*</div>*/}

                    {/* Precio con descuento */}
                    <div className="h2 text_web_color_oscuro_3 mb-0">
                        <Tooltip
                            title={
                                <div>
                                    <div dangerouslySetInnerHTML={{ __html: bestPromo.description_html || bestPromo.name }} />
                                    {isConditionalPrice ? (
                                        <p className="fst-italic mb-0">* precio condicionado al cumplimiento de las condiciones de la promoción.</p>
                                    ) : null}
                                    <p className="fst-italic mb-0">* Promoción aplicada automáticamente</p>
                                </div>
                            }
                            color="pink"
                        >
                            {formatedDiscountedPrice}{isConditionalPrice ? '*' : ''}
                        </Tooltip>
                    </div>
                </div>
            );
        } else {
            return (
                <p className={Number.isNaN(value) ?
                    "h5 text-center text_web_color_oscuro_3" :
                    "h2 text-nowrap text-center text_web_color_oscuro_3"
                }>
                    {formattedPrice}
                </p>
            );
        }
    };

    return (
        <Card
            className="shadow-sm rounded-1 ant-card-action-wrap d-flex flex-column"
            classNames={{ body: "my-auto" }}
            cover={
                <a href={itemUrl}>
                    <CarrouselItem
                        item={item}
                        selectedChild={selectedChild}
                        aspect={item_aspect_card}
                        preview={false}
                    />
                </a>
            }
            // ✅ MEJORA 1: Usar hasVariants para determinar actions
            actions={hasVariants ? [
                <SelectVariantsItem
                    key={item.id}
                    item={item}
                    selectedChild={selectedChild}
                    setSelectedChild={setSelectedChild}
                />,
                cartControl,
            ] : [cartControl]}
        >
            <Meta
                className="my-auto"
                title={<a href={itemUrl}><div className="text-center text-wrap">{getTitle}</div></a>}
                description={
                    <>
                        {renderPrice()}
                        {stockPresentation.badge && (
                            <Tag color={stockPresentation.badge.color} className="d-block text-center mt-2">
                                {stockPresentation.badge.text}
                            </Tag>
                        )}
                        {stockPresentation.message && (
                            <Typography.Paragraph className="text-center text-wrap text-danger fw-light font-weight-light">
                                {stockPresentation.message}{' '}
                                {stockPresentation.ctaLabel && (
                                    <Button
                                        type="link"
                                        size="small"
                                        className="p-0 align-baseline"
                                        onClick={(evt) => {
                                            evt.preventDefault();
                                            evt.stopPropagation();
                                            requestStockAlert(effectiveItem, item);
                                        }}
                                    >
                                        {stockPresentation.ctaLabel}
                                    </Button>
                                )}
                            </Typography.Paragraph>
                        )}
                    </>
                }
            />
        </Card>
    );
};

/** ✅ Componente actualizado que muestra badges de promociones */
export const ItemWithPromotions: React.FC<{ item: Item }> = ({ item }) => {
    const [selectedChild, setSelectedChild] = useState<Item | null>(null);
    const { resolvedTheme } = useThemeMode();
    const ribbonColor = 'var(--catalogue-ribbon-color)';
    const containerClassName = `catalogue-panel__item theme-${resolvedTheme}`;

    // ✅ MEJORA 2: Calcular formatedPrice reactivamente basado en selectedChild
    const formatedPrice = useMemo(() => {
        return getItemPrice(selectedChild || item);
    }, [selectedChild, item]);

    // ✅ MEJORA 2: Obtener promociones del item actual (base o variante seleccionada)
    const currentItem = selectedChild || item;
    const activePromotions = (currentItem.promotions || []) as PromotionLite[];

    // ✅ MEJORA 2: Usar la mejor promoción calculada desde formatedPrice
    const bestPromotion = formatedPrice?.promotion;
    const ribbonText = bestPromotion?.promotion?.badge_label || bestPromotion?.promotion?.name ||
        (activePromotions.length > 0 ? activePromotions[0].badge_label || activePromotions[0].name : null);

    // Si hay promociones activas, mostrar badge ribbon
    const card = (
        <ItemCard
            item={item}
            selectedChild={selectedChild}
            setSelectedChild={setSelectedChild}
        />
    );

    if (ribbonText) {
        return (
            <div className={containerClassName} data-theme={resolvedTheme}>
                <Badge.Ribbon
                    text={ribbonText}
                    className="h6"
                    color={ribbonColor}
                >
                    {card}

                    {/*/!* Si hay múltiples promociones, mostrar badges adicionales *!/*/}
                    {/*{activePromotions.length > 1 && (*/}
                    {/*    <div className="position-absolute top-0 end-0 p-2" style={{zIndex: 1}}>*/}
                    {/*        <Badge*/}
                    {/*            count={`+${activePromotions.length - 1}`}*/}
                    {/*            style={{*/}
                    {/*                backgroundColor: '#f50',*/}
                    {/*                fontSize: '10px',*/}
                    {/*                minWidth: '18px',*/}
                    {/*                height: '18px',*/}
                    {/*                lineHeight: '18px'*/}
                    {/*            }}*/}
                    {/*        />*/}
                    {/*    </div>*/}
                    {/*)}*/}
                </Badge.Ribbon>
            </div>
        );
    }

    return (
        <div className={containerClassName} data-theme={resolvedTheme}>
            {card}
        </div>
    );
};
